import {
    WORKSPACE_MANUFACTURER_SELECTED_KEY,
    WorkspaceManufacturerSelected,
} from 'shared/utils/workspaceUtils'

export interface ExtractedElementIds {
    elementId: string | null
    nodeId: string | null
    // productFamilyId from updatedInput — used when the workspace is at PRODUCTFAMILY level
    // so getProjectProductFamilyView receives the family's ID, not the newly created PT element ID
    productFamilyId: string | null
    source: 'service-worker' | 'message' | 'fallback'
}

/**
 * Extracts elementId, nodeId, and productFamilyId from service worker message data.
 * Prioritizes elementId from addManufacturerProductListingElements.updatedInput.elementId.
 * Falls back to workspaceManufacturerSelectedState values.
 *
 * For nodeId, when the mutation just promoted a catalog PT into the project, the newly
 * created project PT node lives in projectElementNodes (ordered ancestors-first:
 * ASSEMBLY → FAMILY → new PT + siblings). The new PT is the only entry whose elementId
 * matches updatedInput.elementId — siblings come back with elementId: null. We must pick
 * THAT node's id; updatedInput.nodeId echoes back whatever the dialog sent in (the old
 * catalog nodeId), which the backend will then reject when paired with the new PT's
 * elementId in getProjectNuggetContentView ("DocumentTemplateView object is null").
 *
 * productFamilyId is needed when the workspace is at PRODUCTFAMILY level — the returned
 * elementId is a PT-level ID which is incompatible with getProjectProductFamilyView.
 */
export const extractElementIds = (data: any): ExtractedElementIds => {
    // Primary source: service worker addManufacturerProductListingElements response
    const elementIdFromServiceWorker =
        data?.addManufacturerProductListingElements?.updatedInput?.elementId

    // Get workspace state for potential nodeId fallback
    const workspaceState = data?.[
        WORKSPACE_MANUFACTURER_SELECTED_KEY
    ] as WorkspaceManufacturerSelected

    if (elementIdFromServiceWorker) {
        // Locate the newly created PT in projectElementNodes by elementId match.
        // [0] would be the top-level ASSEMBLY, not the new PT.
        const projectElementNodes =
            data?.addManufacturerProductListingElements?.projectElementNodes
        const newPTNode = Array.isArray(projectElementNodes)
            ? projectElementNodes.find(
                  (n: any) => n?.elementId === elementIdFromServiceWorker
              )
            : undefined

        const nodeIdFromUpdatedInput =
            data?.addManufacturerProductListingElements?.updatedInput?.nodeId
        const nodeIdFallback = workspaceState?.nodeId || null
        const productFamilyId =
            data?.addManufacturerProductListingElements?.updatedInput
                ?.productFamilyId || null

        return {
            elementId: elementIdFromServiceWorker,
            // Prefer the new PT's node id when the mutation promoted a PT into the
            // project. Fall back to dialog/workspace-supplied values when the PT was
            // already in the project (projectElementNodes is empty/no match).
            nodeId:
                newPTNode?.id || nodeIdFromUpdatedInput || nodeIdFallback,
            productFamilyId,
            source: 'service-worker',
        }
    }

    // Fallback: get from workspace manufacturer selected state
    if (workspaceState) {
        return {
            elementId: workspaceState.elementId || null,
            nodeId: workspaceState.nodeId || null,
            productFamilyId: null,
            source: 'message',
        }
    }

    return {
        elementId: null,
        nodeId: null,
        productFamilyId: null,
        source: 'fallback',
    }
}